import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { LANDING_LOCALES, makeTr, type LandingLocale, localePath } from "@/lib/landing/locale";
import { persistLanguageCookie } from "@/lib/initialLanguage";
import { Container, CtaLink } from "./primitives";

const focusRing =
  "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--action))] dark:focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * `path` is the page this nav sits on (default the landing page): the language
 * switch keeps it, and on any other page the section links lead back to the
 * landing page's sections.
 */
export function LandingNav({ locale, path = "/" }: { locale: LandingLocale; path?: string }) {
  const tr = makeTr(locale);
  const home = path === "/" ? "" : localePath(locale, "/");
  const { resolvedTheme, setTheme } = useTheme();
  // The theme is only known in the browser: render the toggle's icon after mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = resolvedTheme === "dark";

  const anchors = [
    { href: `${home}#jak-to-funguje`, label: tr("Jak to funguje", "How it works") },
    { href: `${home}#parovani`, label: tr("Párování", "Matching") },
    { href: `${home}#ai-asistent`, label: tr("AI asistent", "AI assistant") },
    { href: `${home}#cenik`, label: tr("Ceník", "Pricing") },
    { href: `${home}#faq`, label: tr("Dotazy", "FAQ") },
  ];
  /** Accountants are their own audience with their own page: a distinct pill, not one more anchor. */
  const accountants = {
    to: localePath(locale, "/for-accountants"),
    label: tr("Pro účetní", "For accountants"),
    current: path === "/for-accountants",
  };
  /** The free browser-only tools: a page of their own, listed after the landing anchors. */
  const tools = {
    to: localePath(locale, "/free-tools"),
    label: tr("Nástroje zdarma", "Free tools"),
    /** Shorter in the desktop bar, where every word counts. */
    short: tr("Nástroje", "Tools"),
    current: path === "/free-tools",
  };
  const pill =
    "inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 font-medium text-[hsl(var(--action))] transition-colors hover:bg-primary/20 dark:text-primary";

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <Container className="flex h-16 items-center justify-between gap-3">
        <div className="flex items-center gap-8">
          <Link to={localePath(locale, "/")} aria-label="Zettl" className={focusRing}>
            <Logo size="sm" />
          </Link>
          <nav aria-label={tr("Stránka", "Page")} className="hidden lg:block">
            <ul className="flex items-center gap-5 whitespace-nowrap text-sm text-muted-foreground">
              {anchors.map((a) => (
                <li key={a.href}>
                  <a
                    href={a.href}
                    className={cn("transition-colors hover:text-foreground", focusRing)}
                  >
                    {a.label}
                  </a>
                </li>
              ))}
              <li>
                <Link
                  to={tools.to}
                  aria-current={tools.current ? "page" : undefined}
                  className={cn(
                    "transition-colors hover:text-foreground aria-[current=page]:text-foreground",
                    focusRing
                  )}
                >
                  {tools.short}
                </Link>
              </li>
              <li>
                <Link
                  to={accountants.to}
                  aria-current={accountants.current ? "page" : undefined}
                  className={cn(pill, focusRing)}
                >
                  {accountants.label}
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3">
          <ul
            aria-label={tr("Jazyk", "Language")}
            className="flex items-center text-xs font-medium text-muted-foreground"
          >
            {LANDING_LOCALES.map((l) => (
              <li key={l}>
                <Link
                  to={localePath(l, path)}
                  hrefLang={l}
                  lang={l}
                  aria-current={l === locale ? "true" : undefined}
                  onClick={() => persistLanguageCookie(l)}
                  className={cn(
                    "inline-flex h-8 min-w-8 items-center justify-center px-1.5 uppercase transition-colors hover:text-foreground",
                    l === locale && "text-foreground",
                    focusRing
                  )}
                >
                  {l}
                </Link>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            aria-label={tr("Přepnout světlý a tmavý vzhled", "Toggle light and dark theme")}
            className={cn(
              "inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
              focusRing
            )}
          >
            {mounted ? (
              isDark ? (
                <Sun className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Moon className="h-4 w-4" aria-hidden="true" />
              )
            ) : (
              <span className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
          <Link
            to={localePath(locale, "/auth")}
            className={cn(
              "inline-flex h-9 items-center whitespace-nowrap px-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground",
              focusRing
            )}
          >
            {tr("Přihlásit se", "Sign in")}
          </Link>
          {/* On phones, and on small laptops where the section links take the
              room, the hero's button is right below; the nav keeps sign-in only. */}
          <CtaLink
            to={localePath(locale, "/auth?tab=register")}
            className="hidden h-9 whitespace-nowrap px-4 sm:inline-flex lg:hidden xl:inline-flex"
          >
            {tr("Vyzkoušet 14 dní zdarma", "Try it free for 14 days")}
          </CtaLink>
        </div>
      </Container>
      {/* Phones: the same anchors as a scrollable row under the bar. */}
      <nav aria-label={tr("Stránka", "Page")} className="border-t border-border/60 lg:hidden">
        <ul className="flex items-center gap-5 overflow-x-auto whitespace-nowrap px-4 py-2 text-sm text-muted-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <li>
            <Link
              to={accountants.to}
              aria-current={accountants.current ? "page" : undefined}
              className={cn(pill, "py-0.5", focusRing)}
            >
              {accountants.label}
            </Link>
          </li>
          {anchors.map((a) => (
            <li key={a.href}>
              <a href={a.href} className={cn("transition-colors hover:text-foreground", focusRing)}>
                {a.label}
              </a>
            </li>
          ))}
          <li>
            <Link
              to={tools.to}
              aria-current={tools.current ? "page" : undefined}
              className={cn(
                "transition-colors hover:text-foreground aria-[current=page]:text-foreground",
                focusRing
              )}
            >
              {tools.label}
            </Link>
          </li>
        </ul>
      </nav>
    </header>
  );
}
